/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { classnames as cx, propsUtils, lodash } from '@ba-ui-toolkit/utils';
import Decorator, { Decoration } from '@ba-ui-toolkit/decorator';
import svgIconStyles from './SVGIcon.scss';

const svgIconClassName = svgIconStyles.svgIconClassName.replace(/"/g, '');

const { sanitize, BaPropTypes } = propsUtils;

export default class SVGIcon extends Component {
	static propTypes = {
		className: PropTypes.string,
		/** Either iconId or icon is required customPropTypes: [string, Object] */
		iconId: BaPropTypes.requiredIf(
			PropTypes.oneOfType([PropTypes.string]),
			props => !Object.prototype.hasOwnProperty.call(props, 'icon')
		),
		/** One of <icon.id> or <ba-graphics icon object> customPropTypes: [string, Object] */
		icon: BaPropTypes.requiredIf(
			PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
			props => !Object.prototype.hasOwnProperty.call(props, 'iconId')
		),
		children: PropTypes.node,
		disabled: PropTypes.bool,
		focusable: PropTypes.bool,
		height: PropTypes.number,
		width: PropTypes.number,
		rotate: PropTypes.number,
		size: PropTypes.oneOf(['tiny', 'small', 'normal', 'large', 'xlarge', 'xxlarge']),
		intent: PropTypes.oneOf(['', 'primary', 'positive', 'danger', 'warning', 'info']),
		themable: PropTypes.bool,
		pad: PropTypes.bool,
		ariaLabelledby: PropTypes.string,
		ariaDescribedby: PropTypes.string,
		alt: PropTypes.string,
		desc: PropTypes.string,
		style: PropTypes.object,
		bidi: PropTypes.bool,
		onKeyUp: PropTypes.func,
		onBlur: PropTypes.func,
		verticalAlign: PropTypes.oneOf([
			'baseline',
			'top',
			'middle',
			'bottom',
			'sub',
			'text-top',
			'unset',
			'inherit',
			'initial',
		]),
		onClick: PropTypes.func,
	};

	static defaultProps = {
		verticalAlign: 'bottom',
		size: 'small',
		focusable: false,
		className: '',
		style: {},
		bidi: false,
	};

	state = {
		showFocusClass: false,
	};

	_isIconObject = () => {
		const { icon } = this.props;

		return typeof icon === 'object' && icon !== null;
	};

	_onKeyUp = e => {
		this.props.onKeyUp && this.props.onKeyUp(e);
		if (e.keyCode === 9) {
			this.setState({ showFocusClass: true });
		}
	};

	_onBlur = e => {
		this.props.onBlur && this.props.onBlur(e);
		this.setState({ showFocusClass: false });
	};

	render() {
		const {
			className,
			iconId: _iconId,
			icon,
			height,
			width,
			rotate,
			size,
			pad,
			intent,
			children,
			focusable,
			themable,
			ariaLabelledby,
			ariaDescribedby,
			alt,
			desc,
			style,
			bidi,
			disabled,
			verticalAlign,
			...other
		} = this.props;

		const iconId = _iconId || (typeof icon === 'string' ? icon : lodash.get(icon, 'id', ''));

		let styles;
		if (height > 0 && width > 0) {
			styles = {
				height,
				width,
			};
		}

		if (rotate) {
			if (!styles) {
				styles = {};
			}
			styles.WebkitTransform = `rotate(${rotate}deg)`;
			styles.transform = `rotate(${rotate}deg)`;
		}
		styles = {
			...styles,
			verticalAlign,
			...style,
		};

		const hasA11yTitle = ariaLabelledby !== undefined && ariaLabelledby !== null;
		const hasA11yDesc = ariaDescribedby !== undefined && ariaDescribedby !== null;
		const isA11y = hasA11yTitle || hasA11yDesc;
		const clickable =
			isA11y ||
			other.onClick ||
			other.onMouseDown ||
			other.onMouseUp ||
			other.onMouseOver ||
			other.onMouseOut ||
			other.onMouseLeave ||
			other.onMouseEnter;

		const classNames = cx(svgIconClassName, {
			[size]: !this._isIconObject(),
			[`intent_${intent}`]: intent && !themable,
			pad,
			clickable,
			focus: this.state.showFocusClass,
			bidi,
			disabled,
		});

		const a11yProps = isA11y
			? {
					role: 'img',
					'aria-labelledby': `${hasA11yTitle ? ariaLabelledby : ''} ${
						hasA11yDesc ? ariaDescribedby : ''
					}`,
			  }
			: '';

		const dimension = {};
		if (this._isIconObject()) {
			const [sWidth, sHeight] = this.props.icon.viewBox.split(' ').splice(-2);
			dimension.width = sWidth;
			dimension.height = sHeight;
		}
		const svg = (
			<svg
				focusable={`${focusable}`}
				{...sanitize(other)}
				className={`${classNames} ${className}`}
				{...dimension}
				style={styles}
				onKeyUp={this._onKeyUp}
				onBlur={this._onBlur}
				{...a11yProps}
			>
				{hasA11yTitle ? <title id={ariaLabelledby}>{alt}</title> : ''}
				{hasA11yDesc ? <desc id={ariaDescribedby}>{desc}</desc> : ''}
				<use
					className={themable ? 'ba-graphics-themable' : ''}
					xmlnsXlink="http://www.w3.org/1999/xlink"
					xlinkHref={`#${iconId}`}
				/>
			</svg>
		);

		if (children) {
			// if (children.type.name === 'Flyout') {
			//     return (
			//         <span>
			//             { svg }
			//             <Decorator style={{ position: 'absolute' }}>
			//                 { children }
			//             </Decorator>
			//         </span>
			//     );
			// }

			return (
				<Decorator>
					{svg}
					{children}
				</Decorator>
			);
		}

		return svg;
	}
}

export class SVGIconDecoration extends Component {
	static propTypes = {
		location: Decoration.propTypes.location,
		x: Decoration.propTypes.x,
		y: Decoration.propTypes.y,
	};

	render() {
		const { location, x, y, ...other } = this.props;

		return (
			<Decoration location={location} x={x} y={y}>
				<SVGIcon size="small" {...other} />
			</Decoration>
		);
	}
}

export function createIcon(icon, size, componetClassName, locationClass, props) {
	if (!icon) {
		return null;
	}

	// if we have a react component pass it through otherwise SVGIcon will now handle icon.id(string) or icon(object)
	const iconComponent = React.isValidElement(icon) ? (
		props ? (
			React.cloneElement(icon, props)
		) : (
			icon
		)
	) : (
		<SVGIcon {...(size ? { size } : {})} focusable={false} icon={icon} {...props} />
	);

	return locationClass ? (
		<span className={cx(`${componetClassName}__icon`, locationClass)}>{iconComponent}</span>
	) : (
		iconComponent
	);
}
